<template>
	<TheBox :title="'Senatsentscheidung'">
		<TheForm
			:model="request"
			:rules="rules"
			:waiting="waiting"
			ref="formRef"
			:id="id"
			:scrollToError="false"
			v-if="currentState !== CaseState.Statements && !showSmallSenatePreliminaryDecision"
		>
			<el-form-item prop="state" class="el-row">
				<el-col :sm="12" :xs="24">
					<el-select v-model="request.state" placeholder="Bitte wählen" @change="changeDecision" :disabled="aiRunning">
						<el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-col>
			</el-form-item>

			<template v-if="selectedDecision === CaseState.Unfounded">
				<BoxAiDecision
					:job="aiJob"
					:disabled="!canGenerate"
					:disabledReason="generateDisabledReason"
					@generate="generateDecision"
					class="senateDecisionForm__ai"
				/>

				<el-row class="flex flex--alignstart senateDecisionForm__text">
					<el-col :xs="24" :sm="12">
						<h3>Begründung</h3>
					</el-col>

					<el-col :xs="24" :sm="12" class="flex flex--justifyend">
						<el-select
							v-if="suggestions.length > 0"
							v-model="selectedSuggestionId"
							:placeholder="texts.aiDecision.previousSuggestion"
							@change="loadSuggestion"
							class="senateDecisionForm__suggestions"
						>
							<el-option v-for="item in suggestionOptions" :key="item.value" :label="item.label" :value="item.value" />
						</el-select>
					</el-col>
				</el-row>

				<el-alert v-if="suggestionWarnings.length > 0" type="warning" :closable="false" show-icon class="senateDecisionForm__warning">
					<template #title>
						<p v-if="!suggestionRuleCheckPassed"><strong>{{ texts.aiDecision.ruleCheckWarning }}</strong></p>
						<ul>
							<li v-for="(warning, index) in suggestionWarnings" :key="index">{{ warning }}</li>
						</ul>
					</template>
				</el-alert>

				<el-form-item label="" prop="text">
					<el-input
						v-model="request.text"
						:autosize="{ minRows: 10 }"
						type="textarea"
						:disabled="aiRunning"
						placeholder="Schreiben Sie hier Ihre Begründung oder nutzen Sie die KI-Funktion, um einen Vorschlag erstellen zu lassen. Diesen können Sie jederzeit bearbeiten."
					/>
				</el-form-item>
			</template>
			<el-alert :title="errorMsg" type="error" v-if="error" />

			<TheButton type="danger" @buttonclick="triggerDialog" :disabled="waiting">Entscheidung treffen</TheButton>
		</TheForm>

		<div v-else-if="showSmallSenatePreliminaryDecision">
			<div class="has-status" :class="`has-status--${$getStateColor('case', currentState)}`">
				{{ $getStateFullLabel('case', currentState) }}
			</div>
			<template v-if="reasonText">
				<h3>Begründung</h3>
				<div class="text">{{ reasonText }}</div>
			</template>
			<el-row v-if="decidedDate" class="decided-info">
				<el-text>Entscheidung getroffen: {{ $formatDate(decidedDate, 'HH:MM, DD.MM.YYYY') }}</el-text>
			</el-row>
		</div>
		<div v-else>
			<div :class="`has-status has-status--red`">
				{{ $getStateFullLabel('decision', DecisionState.GoSmallSenate) }}
			</div>
			<el-row v-if="decidedDate" class="decided-info">
				<el-text>Entscheidung getroffen: {{ $formatDate(decidedDate, 'HH:MM, DD.MM.YYYY') }}</el-text>
			</el-row>
		</div>
	</TheBox>

	<el-dialog
		v-model="openDialogConfirmSenateDecision"
		:title="'Senatsentscheidung'"
		id="dialogConfirmSenateDecision"
		:before-close="(done) => dialogBeforeClose(done, waiting)"
	>
		<div class="has-status" :class="`has-status--red`">
			{{ $getStateFullLabel('decision', selectedDecision === CaseState.Unfounded ? DecisionState.StopSmallSenate : DecisionState.GoSmallSenate) }}
		</div>

		<el-alert type="info" :closable="false" effect="dark" show-icon>
			<template #title>
				<p>Laut Richtlinien des Werberats soll der Mehrheitsentscheidung des Senats entsprochen werden.</p>
				<p>Bitte bestätigen Sie, dass Ihre getroffene Auswahl der Mehrheitsentscheidung des Senats entspricht.</p>
			</template>
		</el-alert>

		<template #footer>
			<div class="button-panel">
				<TheButton type="danger" @buttonclick="saveDecision" :disabled="waiting" :loading="waiting">Bestätigen</TheButton>
				<TheButton type="default" @buttonclick="openDialogConfirmSenateDecision = false">Abbrechen</TheButton>
			</div>
		</template>
	</el-dialog>
</template>

<script lang="ts">
import TheForm from '../TheForm.vue';
import TheButton from '../TheButton.vue';
import TheBox from '../TheBox.vue';
import { texts } from '../../utils/texts';
import { defineComponent, PropType, ref } from 'vue';
import { validateForm, resetForm, dialogBeforeClose } from '@/utils/functions';
import type { FormInstance } from 'element-plus';
import { casePreliminaryDecision, saveCase, changeCaseState } from '../../requests/cases';
import {
	CaseState,
	PreliminaryDecisionRequest,
	ChangeCaseStateRequest,
	DecisionState,
	AiDecisionSuggestionListItemResponse,
	AiJobResponse,
	AiJobStatus,
} from '../../types/types';
import { prepareSearchRequest } from '@/utils/search/searchUtils';
import BoxAiDecision from './BoxAiDecision.vue';
import { useStore } from '@/store';
import { ElMessage, ElMessageBox } from 'element-plus';
import { getAiDecisionSuggestion, getAiDecisionsForCase } from '@/requests/aiDecisions';

interface BoxSenateDecisionData {
	request: {
		state: CaseState | null;
		text: string;
	};
	rules: Object;
	id: string;
	waiting: boolean;
	error: boolean;
	errorMsg: string;
	selectedDecision: CaseState | null;
	CaseState: typeof CaseState;
	DecisionState: typeof DecisionState;
	openDialogConfirmSenateDecision: boolean;
	suggestions: AiDecisionSuggestionListItemResponse[];
	selectedSuggestionId: string | null;
	suggestionWarnings: string[];
	suggestionRuleCheckPassed: boolean;
	startedHere: boolean;
}

export default defineComponent({
	name: 'BoxSenateDecision',
	data(): BoxSenateDecisionData {
		return {
			request: {
				state: null,
				text: '',
			},
			rules: {
				state: [{ required: true, message: texts.formErrors.reqMissing, trigger: 'change' }],
				text: [
					{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' },
					{ validator: this.validateReasoning, trigger: 'change' },
				],
			},
			id: 'senateDecisionForm',
			waiting: false,
			error: false,
			errorMsg: texts.cases.saveFailed,
			selectedDecision: null,
			CaseState,
			DecisionState,
			openDialogConfirmSenateDecision: false,
			suggestions: [],
			selectedSuggestionId: null,
			suggestionWarnings: [],
			suggestionRuleCheckPassed: true,
			startedHere: false,
		};
	},
	components: {
		TheBox,
		TheForm,
		TheButton,
		BoxAiDecision,
	},
	setup() {
		const formRef = ref<FormInstance | null>(null);
		const store = useStore();

		return {
			formRef,
			store,
		};
	},
	props: {
		caseId: {
			type: String,
			required: true,
		},
		currentState: {
			type: String as PropType<CaseState>,
			required: true,
		},
		existingDecisions: {
			type: Array,
			default: () => [],
		},
		decidedDate: {
			type: null as unknown as PropType<string | null>,
			default: '',
		},
		showSmallSenatePreliminaryDecision: {
			type: Boolean,
			default: false,
		},
		reasonText: {
			type: String,
			default: '',
		},
	},
	emits: ['reload'],
	computed: {
		statusOptions() {
			return [
				{ label: 'Fall im Werberat behandeln', value: CaseState.Statements },
				{ label: 'Entscheidung: Als offensichtlich unbegründet abgewiesen', value: CaseState.Unfounded },
			];
		},
		texts() {
			return texts;
		},
		aiJob(): AiJobResponse | null {
			return this.store.getters['aiDecision/jobForCase'](this.caseId);
		},
		aiRunning(): boolean {
			return this.store.getters['aiDecision/isRunning'](this.caseId);
		},
		/** Voten mit Freitext — ohne mindestens einen bricht das Backend in Schritt 1 ohne Modellaufruf ab. */
		reasonTextCount(): number {
			return (this.existingDecisions as any[]).filter((decision) => decision?.reasonText && decision.reasonText.trim().length > 0).length;
		},
		canGenerate(): boolean {
			return !this.aiRunning && this.selectedDecision === CaseState.Unfounded && this.reasonTextCount > 0;
		},
		generateDisabledReason(): string {
			if (this.aiRunning) return texts.aiDecision.disabledRunning;
			if (this.reasonTextCount === 0) return texts.aiDecision.disabledNoReasonTexts;
			return '';
		},
		suggestionOptions() {
			return this.suggestions.map((suggestion) => {
				const timestamp = new Date(suggestion.createdAt).toLocaleString('de-AT', {
					day: '2-digit',
					month: '2-digit',
					year: 'numeric',
					hour: '2-digit',
					minute: '2-digit',
				});
				return { value: suggestion.id, label: `${timestamp} — Unbegründet` };
			});
		},
	},
	methods: {
		async triggerDialog() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.openDialogConfirmSenateDecision = true;
		},
		async saveDecision() {
			this.waiting = true;
			if (this.selectedDecision === CaseState.Unfounded) {
				let searchRequest = prepareSearchRequest(this.request);

				const requestPreliminaryDecision = {
					caseId: String(this.caseId),
					fromSmallSenate: true,
					...searchRequest,
				} as PreliminaryDecisionRequest;

				const result = await casePreliminaryDecision(requestPreliminaryDecision);
				if (result.success) {
					this.handleSuccess();
				} else {
					this.error = true;
				}
			} else {
				const requestUpdateCaseState = {
					caseId: String(this.caseId),
					state: this.selectedDecision,
				} as ChangeCaseStateRequest;

				const result = await changeCaseState(requestUpdateCaseState);
				if (result.success) {
					this.handleSuccess();
				} else {
					this.error = true;
				}
			}

			this.openDialogConfirmSenateDecision = false;
			this.waiting = false;
		},
		handleSuccess() {
			resetForm(this.formRef, this.id);
			this.error = false;
			this.selectedDecision = null;
			// Der Fall hat sich gerade geändert — eine Fehlermeldung zum alten Stand wäre irreführend
			this.store.dispatch('aiDecision/discardFailure', this.caseId);
			this.$emit('reload');
		},
		async generateDecision() {
			if (!this.canGenerate) return;

			if (this.request.text.trim().length > 0) {
				try {
					await ElMessageBox.confirm(texts.aiDecision.overwriteConfirm, 'KI-Begründung', {
						confirmButtonText: 'Ersetzen',
						cancelButtonText: 'Abbrechen',
						type: 'warning',
					});
				} catch {
					return;
				}
			}

			this.startedHere = true;
			await this.store.dispatch('aiDecision/start', { caseId: this.caseId, decision: DecisionState.StopSmallSenate });
		},
		/** Nur Vorschläge der Senatsentscheidung — die Vorschläge des Werberats gehören in dessen Auswahl. */
		async loadSuggestions() {
			const result = await getAiDecisionsForCase(this.caseId);
			if (result.success) {
				this.suggestions = result.data.suggestions.filter((suggestion) => suggestion.requestedDecision === DecisionState.StopSmallSenate);
			}
		},
		async loadSuggestion(suggestionId: string) {
			const result = await getAiDecisionSuggestion(suggestionId);
			if (!result.success) {
				ElMessage({ message: texts.aiDecision.loadFailed, type: 'error' });
				return;
			}
			this.request.text = result.data.text;
			this.suggestionWarnings = result.data.warnings;
			this.suggestionRuleCheckPassed = result.data.ruleCheckPassed;
			this.selectedSuggestionId = suggestionId;
		},
		changeDecision(val: any) {
			this.error = false;
			this.selectedDecision = val.value;
		},
		validateReasoning(rule: any, value: any, callback: any) {
			if (this.selectedDecision && !value) {
				callback(new Error(this.texts.formErrors.reqMissing));
			} else return true;
		},
		dialogBeforeClose,
	},
	watch: {
		aiJob: {
			deep: true,
			async handler(job: AiJobResponse | null) {
				if (!job || job.status === AiJobStatus.Running) return;
				await this.loadSuggestions();
				if (job.status !== AiJobStatus.Succeeded || !job.suggestionId) return;

				if (this.startedHere) {
					// In dieser Ansicht gestartet und das Überschreiben bereits bestätigt
					await this.loadSuggestion(job.suggestionId);
					this.startedHere = false;
				} else {
					// Fremd gestartet oder Ansicht zwischenzeitlich verlassen: nur bereitstellen,
					// nicht ins Feld springen — dort könnte inzwischen getippter Text stehen
					ElMessage({ message: texts.aiDecision.finished, type: 'success' });
				}
			},
		},
	},
	async mounted() {
		await this.loadSuggestions();
		// Hängt sich an einen serverseitig laufenden Job — deckt Reload, Navigation und fremde Läufe ab
		await this.store.dispatch('aiDecision/attach', this.caseId);
	},
});
</script>

<style lang="scss" scoped>
#senateDecisionForm {
	.senateDecisionForm__ai {
		margin-top: 1rem;
	}

	.senateDecisionForm__suggestions {
		max-width: 320px;
	}

	.senateDecisionForm__warning {
		margin-bottom: 1rem;

		ul {
			margin: 0.25rem 0 0;
			padding-left: 1.25rem;
		}
	}

	.senateDecisionForm__text {
		margin-top: 2rem;

		h3 {
			margin: 0;
		}
	}

	.el-button.is-round.is-loading:disabled {
		opacity: 1;
	}

	.el-textarea {
		margin-top: 1rem;
	}
}

.has-status {
	margin-bottom: 1.5rem;
}

#dialogConfirmSenateDecision {
	.el-alert {
		background-color: var(--el-color-lightgrey-3);
	}
}
</style>
